import { Button, LargeTitle, makeStyles, Text } from "@fluentui/react-components";
import { mergeStyles } from '@fluentui/merge-styles';
import IHeroProps from "./IHeroProps";
import { Link } from "react-router-dom";
import { dolphinTokens } from "../../utils/Theme";

const useStyles = makeStyles({
    hero: {
        position: "relative",
        height: "600px",
        backgroundPosition: "center",
        backgroundSize: "cover",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center"
    },
    overlay: {
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)"
    },
    container: {
        zIndex: 1,
        height: "100%"
    },
    imageContainer: {
        width: "60%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        
        "@media (max-width: 768px)": {
            width: "80%"
        }
    },
    videoContainer: {
        gridRow: '1 / -1',
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 1,
    },
    title: {
        color: "white",
        textAlign: "center",
        marginBottom: "1rem",
    },
    description: {
        color: "white",
        textAlign: "center",
        maxWidth: "800px",
    },
    backgroundVideo: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    heroContainer: {
        gridRow: '1 / -1',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: 'white',
        padding: '2rem',
        maxWidth: '800px',
    },
    ctaLink: {
        textDecoration: "none"
    },
    ctaButton: {
        backgroundColor: 'white !important',
        color: dolphinTokens.colorDarkBlue,
        padding: '0.75rem 1.5rem !important',
        fontWeight: '600 !important',
        borderRadius: '0.5rem !important',
        border: 'none !important',
        cursor: 'pointer !important',
        transition: 'transform 0.2s ease !important',
        
        '&:hover': {
            transform: 'translateY(-3px) !important',
            color: dolphinTokens.colorDarkBlue60,
        },

        '&:active': {
            color: `${dolphinTokens.colorLightBlue} !important`,
        },
    }
});

const HeroWithImage = (props: IHeroProps) => {
    const styles = useStyles();

    const heroClass = mergeStyles(styles.hero, {
        backgroundImage: `url(${props.imageUri})`
    });

    return (
        <section className={heroClass}>
            <div className={styles.overlay} />
            <div className={`${styles.container} ${styles.imageContainer}`}>
                <LargeTitle block as="h1" className={styles.title}>
                    {props.title}
                </LargeTitle>
                <Text block as="h3" className={styles.description} size={500}>
                    {props.subtitle}
                </Text>
            </div>
        </section>
    );
};

const HeroWithVideo = (props: IHeroProps) => {
    const styles = useStyles();

    return (
            <div className={styles.hero}>
                <div className={`${styles.container} ${styles.videoContainer}`}>
                    <video autoPlay loop muted playsInline
                        className={styles.backgroundVideo}
                        poster={props.videoPreviewUri}
                        preload="auto"
                        controls={false}
                        >
                            <source type="video/mp4" src={props.videoUri} />
                    </video>
                    <div className={styles.overlay} />
                </div>
    
                <div className={styles.heroContainer}>
                    <LargeTitle as="h1" align="center" block>
                        {props.title}
                    </LargeTitle>
                    <Text as="h3" size={500} align="center" block>
                        {props.subtitle}
                    </Text>
                    {
                        props.cta &&
                        <Link to={props.cta?.url} className={styles.ctaLink}>
                            <Button appearance="primary" className={styles.ctaButton}>
                                {props.cta?.text}
                            </Button>
                        </Link>
                    }
                </div>
            </div>
        );
};

export default function Hero(props: IHeroProps) {
    if (!!props.imageUri && !!props.videoUri) {
        throw new Error("Hero component requires either an image or a video URI, but not both.");
    }

    if (!!props.imageUri) {
        return <HeroWithImage {...props} />;
    }

    if (!!props.videoUri && !props.videoPreviewUri) {
        throw new Error("Hero component requires a video preview URI when using a video URI.");
    }

    if (!!props.videoUri) {
        return <HeroWithVideo {...props} />;
    }

    throw new Error("Hero component requires either an image or a video URI.");
}